/**
 * Step 4: Servicios y Honorarios del Cliente
 * Versión refactorizada con TanStack Form
 */
import { useState, useCallback, useMemo } from 'react'
import { useStore } from '@tanstack/react-store'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Receipt, Tag } from 'lucide-react'
import { formatCurrency, cn } from '@/lib/utils'
import { ServiciosMultiSelector } from '@/components/ui/servicios-multi-selector'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { useEtiquetasCliente } from '@/core/hooks/useEtiquetasCliente'
import { usePermissions } from '@/hooks/usePermissions'
import type { ClienteForm } from '../useClienteForm'
import type { ServicioConPrecio } from '../clienteSchema'
import type { Servicio } from '@/types/database'

interface Usuario {
  id: string
  nombre: string | null
  email: string
}

interface StepServiciosProps {
  form: ClienteForm
  serviciosDisponibles: Servicio[]
  usuarios: Usuario[]
  /** Opt-in: solo lo pasa el caller que reconcilia las etiquetas después del submit. */
  mostrarEtiquetas?: boolean
}

const SIN_SERVICIOS: ServicioConPrecio[] = []

export function StepServiciosNew({
  form,
  serviciosDisponibles,
  usuarios,
  mostrarEtiquetas = false,
}: StepServiciosProps) {
  const { puede } = usePermissions()
  const { data: catalogoEtiquetas = [] } = useEtiquetasCliente()
  const opcionesEtiquetas = useMemo(
    () => catalogoEtiquetas.map((e) => ({ value: e.id, label: e.nombre })),
    [catalogoEtiquetas]
  )

  // Los servicios se leen del store del form, la única fuente: la precarga de la edición puede llegar
  // después de que el usuario agregó otros, y el paso tiene que mostrar y editar la lista completa.
  const serviciosConPrecio = useStore(form.store, (s) => s.values.serviciosConPrecio) ?? SIN_SERVICIOS

  // El switch se ve prendido con servicios cargados. Sin servicios, lo prende el usuario para elegirlos.
  const [honorarioActivado, setHonorarioActivado] = useState(false)
  const tieneHonorarioMensual = honorarioActivado || serviciosConPrecio.length > 0

  // El tipo de persona filtra los servicios disponibles. Se lee suscripto al store: puede cambiar con
  // la ficha del cliente mientras el paso está montado.
  const tipoPersona = useStore(form.store, (s) => s.values.tipo_persona)

  // Filtrar servicios según el tipo de persona del cliente
  const serviciosFiltrados = useMemo(() => {
    return serviciosDisponibles.filter((servicio) => {
      switch (tipoPersona) {
        case 'fisica':
          return servicio.aplica_persona_fisica === true
        case 'juridica':
          return servicio.aplica_persona_juridica === true
        case 'sucesion_indivisa':
          return servicio.aplica_sucesion_indivisa === true
        default:
          // Si no hay tipo definido, mostrar todos los servicios
          return true
      }
    })
  }, [serviciosDisponibles, tipoPersona])

  // Calcular total de servicios contratados
  const calcularTotalServicios = useCallback((): number => {
    return serviciosConPrecio.reduce((sum: number, s: ServicioConPrecio) => sum + s.precio, 0)
  }, [serviciosConPrecio])

  const actualizarServicios = useCallback((nuevosServicios: ServicioConPrecio[]) => {
    form.setFieldValue('serviciosConPrecio', nuevosServicios)
  }, [form])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-2 border-b">
        <div className="p-2 rounded-lg bg-primary/10">
          <Receipt className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-medium">Servicios y Honorarios</h3>
          <p className="text-sm text-muted-foreground">Configura los servicios del cliente</p>
        </div>
      </div>

      {/* Honorarios: solo con permiso de verlos y de editarlos. Sin los dos, el guardado no toca los servicios. */}
      {puede.editarHonorariosCliente && puede.verHonorariosCliente && (
        <>
          {/* Toggle Honorarios Mensuales */}
          <div className="flex items-start gap-4 p-4 rounded-lg border bg-muted/30">
            <Switch
              id="tiene-honorario-mensual"
              checked={tieneHonorarioMensual}
              onCheckedChange={(checked) => {
                setHonorarioActivado(checked)
                if (!checked) {
                  actualizarServicios([])
                }
              }}
            />
            <div className="space-y-1">
              <Label htmlFor="tiene-honorario-mensual" className="text-base font-medium cursor-pointer">
                Tiene honorarios mensuales
              </Label>
              <p className="text-sm text-muted-foreground">
                Activa esta opción si el cliente tiene servicios con facturación recurrente.
              </p>
            </div>
          </div>

          {/* Selector de Servicios */}
          {tieneHonorarioMensual && (
            <div className="space-y-4">
              <Label className="text-base font-semibold">Servicios Mensuales</Label>

              <ServiciosMultiSelector
                servicios={serviciosFiltrados}
                value={serviciosConPrecio}
                onChange={actualizarServicios}
                disabled={!tieneHonorarioMensual}
              />

              {/* Total */}
              {serviciosConPrecio.length > 0 && (
                <div className="flex items-center justify-between p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <span className="text-sm font-medium text-muted-foreground">Total Servicios Contratados</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(calcularTotalServicios())}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Responsable(s): solo con el permiso de responsables. Sin él, el campo queda en undefined. */}
      {puede.asignarResponsablesCliente && (
        <form.Field name="responsables">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0
            const selectedIds: string[] = field.state.value || []

            const toggleUsuario = (usuarioId: string) => {
              const newIds = selectedIds.includes(usuarioId)
                ? selectedIds.filter(id => id !== usuarioId)
                : [...selectedIds, usuarioId]
              field.handleChange(newIds)
            }

            return (
              <div className="space-y-2">
                <Label className={cn('text-base font-semibold', hasError && 'text-destructive')}>
                  Responsable(s) *
                </Label>
                <div className={cn(
                  'rounded-md border p-3 space-y-2',
                  hasError && 'border-destructive'
                )}>
                  {usuarios.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No hay usuarios disponibles</p>
                  ) : (
                    usuarios.map((usuario) => {
                      const isSelected = selectedIds.includes(usuario.id)
                      return (
                        <label
                          key={usuario.id}
                          className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleUsuario(usuario.id)}
                          />
                          {usuario.nombre || usuario.email}
                        </label>
                      )
                    })
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Persona(s) del estudio que estarán a cargo de este cliente.
                </p>
              </div>
            )
          }}
        </form.Field>
      )}

      {/* Etiquetas: solo del catálogo existente, sin alta inline como en la ficha. Oculto salvo
          que el caller reconcilie lo elegido después del submit (ver `mostrarEtiquetas`). */}
      {mostrarEtiquetas && puede.editarCliente && (
        <form.Field name="etiquetas">
          {(field) => (
            <div className="space-y-2">
              <Label className="text-base font-semibold flex items-center gap-1.5">
                <Tag className="h-4 w-4 text-muted-foreground" />
                Etiquetas
              </Label>
              <MultiSelectFilter
                options={opcionesEtiquetas}
                value={field.state.value ?? []}
                onChange={(v) => field.handleChange(v)}
                label="Etiquetas"
                emptyLabel="ninguna"
                searchable
                searchPlaceholder="Buscar etiqueta..."
                triggerClassName="w-full sm:w-64"
              />
            </div>
          )}
        </form.Field>
      )}

      {/* Observaciones */}
      <form.Field name="descripcion_tarea">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor="descripcion_tarea" className="text-base font-semibold">
              Observaciones
            </Label>
            <Textarea
              id="descripcion_tarea"
              placeholder="Notas sobre el cliente..."
              value={field.state.value || ''}
              onChange={(e) => field.handleChange(e.target.value)}
              className="min-h-[100px] resize-none"
            />
          </div>
        )}
      </form.Field>
    </div>
  )
}
